import { TableSortDirection } from 'eone-ui/tables/table-constants';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocalStorage } from 'react-use';
import type { InitialTableState } from './types';

// Experiemental: helper to enhance useLocalStorage with versioning-GC feature
// to clear outdated persistent storage
const enableVersioningGC = false;
// -v0.0.1 or -v0.0.1-alpha.1 or -v0.0.1-beta.1
const regexStrictVersionLike = /[-_]v\d+\.\d+\.\d+([-_](alpha|beta)\.\d+)?/i;
const useLocalStorageWithVersioningGC = (key: string, initialValue?: any) => {
  if (enableVersioningGC) {
    const isStrictVersionLike = regexStrictVersionLike.test(key);
    const shouldGC = isStrictVersionLike;
    if (shouldGC) {
      const withVersionRemoved = key.replace(regexStrictVersionLike, '');
      const keysToGC = Object.keys(localStorage).filter((k) => {
        return k !== key && k.replace(regexStrictVersionLike, '') === withVersionRemoved;
      });
      keysToGC.forEach((k) => localStorage.removeItem(k));
    }
  }
  return useLocalStorage(key, initialValue);
};

const COLUMN_WIDTHS_PREFIX = 'AdvanceTableState';

/** Read column widths directly from localStorage (avoids state-driven re-render cycles) */
function readColumnWidths(id: string, fallback: Record<string, number>): Record<string, number> {
  try {
    const raw = localStorage.getItem(`${COLUMN_WIDTHS_PREFIX}-${id}-columnWidths`);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

/** Write column widths directly to localStorage */
function writeColumnWidths(id: string, widths: Record<string, number>) {
  try {
    localStorage.setItem(`${COLUMN_WIDTHS_PREFIX}-${id}-columnWidths`, JSON.stringify(widths));
  } catch {
    // ignore quota errors
  }
}

const PREFIX = 'AdvanceTableState';
const useAdvanceTableStateCache = <D extends object>(
  id: string,
  initial?: InitialTableState<D>,
) => {
  // un need cache
  const [search, setSearch] = useState('');

  const [sort, setSort] = useLocalStorageWithVersioningGC(`${PREFIX}-${id}-sort`, {
    FieldName: initial?.InitialSortField || '',
    Direction: initial?.InitialSortDirection || TableSortDirection.Ascending,
  });

  const [columns, setColumns] = useLocalStorageWithVersioningGC(
    `${PREFIX}-${id}-columns`,
    initial?.InitialColumns || [],
  );

  const [filterValues, setFilterValues] = useLocalStorageWithVersioningGC(
    `${PREFIX}-${id}-filterValues`,
    initial?.InitialFilterValues || {},
  );

  const [columnOrder, setColumnOrder] = useLocalStorageWithVersioningGC(
    `${PREFIX}-${id}-columnOrder`,
    initial?.InitialColumnOrder || [],
  );

  // Column widths use a ref + direct localStorage to avoid triggering
  // eone-ui table.jsx's column-reset effect (which watches TableState.ColumnWidths).
  // The effect resets all widths when the ColumnWidths reference changes, creating a cycle.
  const columnWidthsRef = useRef<Record<string, number>>(
    readColumnWidths(id, initial?.InitialColumnWidths || {}),
  );

  const onColumnResize = useCallback(
    ({ FieldName, Width }: { FieldName: string; Width: number }) => {
      columnWidthsRef.current = { ...columnWidthsRef.current, [FieldName]: Width };
      writeColumnWidths(id, columnWidthsRef.current);
    },
    [id],
  );

  const changeSort = useCallback(
    (fieldName) => {
      if (fieldName === sort?.FieldName) {
        const direction =
          sort?.Direction === TableSortDirection.Ascending
            ? TableSortDirection.Descending
            : TableSortDirection.Ascending;
        setSort({ FieldName: fieldName, Direction: direction });
      } else {
        setSort({ FieldName: fieldName, Direction: TableSortDirection.Ascending });
      }
    },
    [setSort, sort?.Direction, sort?.FieldName],
  );

  const changeFilter = useCallback(
    (fieldName, values) => {
      const newFilterValues = { ...filterValues };
      newFilterValues[fieldName] = values;
      setFilterValues(newFilterValues);
    },
    [filterValues, setFilterValues],
  );

  const clearFilterValues = useCallback(() => {
    setFilterValues({});
  }, [setFilterValues]);

  const resetColumnLayout = useCallback(() => {
    columnWidthsRef.current = initial?.InitialColumnWidths || {};
    writeColumnWidths(id, columnWidthsRef.current);
    setColumnOrder(initial?.InitialColumnOrder || []);
    setSort({
      FieldName: initial?.InitialSortField || '',
      Direction: initial?.InitialSortDirection || TableSortDirection.Ascending,
    });
  }, [
    id,
    initial?.InitialColumnWidths,
    initial?.InitialColumnOrder,
    setColumnOrder,
    setSort,
    initial?.InitialSortField,
    initial?.InitialSortDirection,
  ]);

  // ColumnWidths is a stable ref value — eone-ui reads it once for InitialColumnWidths.
  // OnColumnResize persists directly to localStorage without state re-renders.
  return useMemo(
    () => ({
      Search: search,
      Sort: sort,
      Columns: columns,
      FilterValues: filterValues,
      SetSearch: setSearch,
      SetSort: changeSort,
      SetColumns: setColumns,
      SetFilterValues: changeFilter,
      ClearFilterValues: clearFilterValues,
      ColumnWidths: columnWidthsRef.current,
      ColumnOrder: columnOrder,
      OnColumnResize: onColumnResize,
      SetColumnOrder: setColumnOrder,
      ResetColumnLayout: resetColumnLayout,
    }),
    [
      changeFilter,
      changeSort,
      clearFilterValues,
      columns,
      columnOrder,
      filterValues,
      onColumnResize,
      resetColumnLayout,
      search,
      setColumnOrder,
      setColumns,
      sort,
    ],
  );
};

export { useAdvanceTableStateCache };
